import { useEffect, useState, useRef } from 'react';
import { onSnapshot, query, orderBy, limit, where } from 'firebase/firestore';
import { feedbackCollection } from '../firebase';
import { filterLegitimateDocs } from '../utils/feedbackFilters';
import { formatDateString } from '../utils/dateFormat';

/**
 * Human-readable timestamp for a feedback row on the selected report day.
 * Always includes the calendar date when the report day is not today, so
 * stale rows cannot be mistaken for "just now on today's board".
 */
export const formatFeedbackTimestamp = (when, collectionDate) => {
  if (!when) return '';
  const selectedDay = collectionDate || formatDateString(when);
  const todayStr = formatDateString(new Date());
  const diff = Date.now() - when.getTime();

  if (selectedDay === todayStr) {
    if (diff < 30_000) return 'just now';
    if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  }

  const datePart = when.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const timePart = when.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return selectedDay === todayStr ? timePart : `${datePart} · ${timePart}`;
};

const parseFeedbackDate = (raw) => {
  if (!raw) return null;
  if (raw.toDate) {
    try { return raw.toDate(); } catch { return null; }
  }
  if (typeof raw === 'string') {
    const cleaned = raw.split('UTC')[0].trim().replace(' at ', ' ');
    const d = new Date(cleaned);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const AREA_LABELS = {
  1: 'CHARMINAR',
  2: 'KARWAN',
  3: 'SANTOSH NAGAR',
  4: 'GOSHAMAHAL',
  5: 'SECUNDERABAD',
  6: 'POLICE STATIONS HYDERABAD',
  7: 'POLICE STATIONS CYBERABAD',
};

const shape = (docSnap) => {
  const data = docSnap.data();
  const when = parseFeedbackDate(data.feedback_collection_DateTime);
  const collectionDate = data.feedback_collection_Date || (when ? formatDateString(when) : '');
  const areaNum = parseInt(data.shop_area, 10);
  return {
    id: docSnap.id,
    when,
    collectionDate,
    relative: formatFeedbackTimestamp(when, collectionDate),
    stars: parseInt(data.feedback_stars, 10) || 0,
    supervisorName: data.feedback_collector_name || '—',
    shopName: data.shop_name || '—',
    area: AREA_LABELS[areaNum] || '',
    areaNum: Number.isFinite(areaNum) ? areaNum : null,
    collectorId: data.feedback_collector_ixora_userID || '',
  };
};

/**
 * Live feedback for one report day (feedback_collection_Date), newest first.
 * Pass pulseOnArrival=true only when watching calendar-today so historical
 * date picks do not flash "new" on first load.
 */
export function useNewFeedbackStream({ size = 8, dateString, pulseOnArrival = false } = {}) {
  const [items, setItems] = useState([]);
  const [latest, setLatest] = useState(null);
  const seenTopId = useRef(null);
  const firstSnapshotRef = useRef(true);

  useEffect(() => {
    if (!dateString) {
      setItems([]);
      setLatest(null);
      return undefined;
    }

    firstSnapshotRef.current = true;
    seenTopId.current = null;

    const q = query(
      feedbackCollection,
      where('feedback_collection_Date', '==', dateString),
      orderBy('feedback_collection_DateTime', 'desc'),
      limit(size),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const next = filterLegitimateDocs(snap.docs).map(shape);
        setItems(next);
        const top = next[0];
        if (top && top.id !== seenTopId.current) {
          if (!firstSnapshotRef.current && pulseOnArrival) setLatest(top);
          seenTopId.current = top.id;
        }
        firstSnapshotRef.current = false;
      },
      (err) => {
        console.error('Feedback stream subscription failed:', err);
        setItems([]);
      },
    );
    return unsub;
  }, [size, dateString, pulseOnArrival]);

  return { items, latest };
}
